import { Download, Search, X } from "lucide-react";
import { MATERIAL_CATEGORIES } from "./MaterialConstants";

export const EMPTY_RECORD_FILTERS = { from: "", to: "", category: "all", material_id: "all", party: "all", search: "" };

export const applyRecordFilters = (records, filters, { materials, partyKey, getMaterialName }) => {
  const term = filters.search.trim().toLowerCase();
  return records.filter((r) => {
    const cat = r.category || materials.find((m) => m.id === r.material_id)?.category || "construction";
    if (filters.from && r.date < filters.from) return false;
    if (filters.to && r.date > filters.to) return false;
    if (filters.category !== "all" && cat !== filters.category) return false;
    if (filters.material_id !== "all" && r.material_id !== filters.material_id) return false;
    if (filters.party !== "all" && (r[partyKey] || "") !== filters.party) return false;
    if (term) {
      const hay = [r.grn_number, r.issue_number, r.challan_number, r.supplier_name, r.issued_to, r.purpose, r.remarks, r.vehicle_number, getMaterialName(r.material_id)]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  });
};

const fieldCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary";
const labelCls = "block text-[10px] font-medium text-slate-600 mb-1 uppercase tracking-widest";

export const RecordFilters = ({ prefix, filters, setFilters, records, materials, partyKey, partyLabel, summary, onExport }) => {
  const set = (k, v) => setFilters({ ...filters, [k]: v });
  const parties = [...new Set(records.map((r) => r[partyKey]).filter(Boolean))].sort();
  const matOptions = materials
    .filter((m) => filters.category === "all" || (m.category || "construction") === filters.category)
    .sort((a, b) => a.name.localeCompare(b.name));
  const active = JSON.stringify(filters) !== JSON.stringify(EMPTY_RECORD_FILTERS);

  return (
    <div className="stat-card mb-4" data-testid={`${prefix}-filters`}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div>
          <label className={labelCls}>From Date</label>
          <input type="date" value={filters.from} onChange={(e) => set("from", e.target.value)} className={`${fieldCls} font-mono`} data-testid={`${prefix}-filter-from`} />
        </div>
        <div>
          <label className={labelCls}>To Date</label>
          <input type="date" value={filters.to} onChange={(e) => set("to", e.target.value)} className={`${fieldCls} font-mono`} data-testid={`${prefix}-filter-to`} />
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value, material_id: "all" })} className={fieldCls} data-testid={`${prefix}-filter-category`}>
            <option value="all">All Categories</option>
            {MATERIAL_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Material</label>
          <select value={filters.material_id} onChange={(e) => set("material_id", e.target.value)} className={fieldCls} data-testid={`${prefix}-filter-material`}>
            <option value="all">All Materials</option>
            {matOptions.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>{partyLabel}</label>
          <select value={filters.party} onChange={(e) => set("party", e.target.value)} className={fieldCls} data-testid={`${prefix}-filter-party`}>
            <option value="all">All</option>
            {parties.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Search</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={filters.search} onChange={(e) => set("search", e.target.value)} placeholder="No., challan, remarks..." className={`${fieldCls} pl-8`} data-testid={`${prefix}-filter-search`} />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-200 text-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-600" data-testid={`${prefix}-filter-summary`}>
          <span>Showing <strong className="text-slate-900 font-mono">{summary.count}</strong> of <span className="font-mono">{records.length}</span></span>
          {summary.qty !== null && <span>Total Qty: <strong className="text-slate-900 font-mono">{summary.qty.toFixed(2)} {summary.unit}</strong></span>}
          <span>Total Value: <strong className="text-slate-900 font-mono">₹{summary.value.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</strong></span>
        </div>
        <div className="flex items-center gap-4">
          {active && (
            <button onClick={() => setFilters(EMPTY_RECORD_FILTERS)} className="flex items-center gap-1 text-xs text-error hover:underline" data-testid={`${prefix}-filter-clear`}>
              <X className="w-3 h-3" /> Clear filters
            </button>
          )}
          <button onClick={onExport} disabled={!summary.count} className="btn-outline flex items-center gap-2 text-xs py-1.5 disabled:opacity-50" data-testid={`${prefix}-export-btn`}>
            <Download className="w-4 h-4" /> Export Excel
          </button>
        </div>
      </div>
    </div>
  );
};
